import { useId, useState, type FormEvent } from 'react'

import { ApiError } from '@/api/client'
import { useCreatePlaylist, useUpdatePlaylist } from '@/api/hooks'
import type { Playlist } from '@/api/types'
import { Button, Dialog, Input } from '@/components/ui'

import { describeError } from './helpers'

export interface PlaylistFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Omit to create a new playlist; pass one to rename or re-describe it. */
  playlist?: Playlist
  onSaved?: (playlist: Playlist) => void
}

/** Create or rename — the fields and the validation are identical, so is the form. */
export function PlaylistFormDialog({
  open,
  onOpenChange,
  playlist,
  onSaved,
}: PlaylistFormDialogProps) {
  const isEdit = playlist !== undefined
  const formId = `playlist-form-${useId()}`

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [session, setSession] = useState<string | null>(null)

  const create = useCreatePlaylist()
  const update = useUpdatePlaylist()
  const pending = create.isPending || update.isPending

  /**
   * Seed the fields when the dialog opens, keyed by which playlist it opened for.
   * Deliberately not an effect: an effect would also have to depend on the
   * `playlist` object, and a background refetch handing back a new object would
   * then wipe out whatever the user had typed.
   */
  const opensFor = playlist ? String(playlist.id) : 'new'
  if (open && session !== opensFor) {
    setSession(opensFor)
    setName(playlist?.name ?? '')
    setDescription(playlist?.description ?? '')
    setNameError(null)
    setFormError(null)
  } else if (!open && session !== null) {
    setSession(null)
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    const trimmedName = name.trim()
    if (!trimmedName) {
      setNameError('Give your playlist a name.')
      return
    }

    setNameError(null)
    setFormError(null)

    try {
      const body = { name: trimmedName, description: description.trim() }
      const saved = playlist
        ? await update.mutateAsync({ id: playlist.id, ...body })
        : await create.mutateAsync(body)
      onOpenChange(false)
      onSaved?.(saved)
    } catch (error) {
      const fieldMessage = error instanceof ApiError ? error.fieldError('name') : undefined
      if (fieldMessage) setNameError(fieldMessage)
      else setFormError(describeError(error, 'Could not save the playlist. Please try again.'))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? 'Rename playlist' : 'New playlist'}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            {isEdit ? 'Save changes' : 'Create playlist'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={nameError ?? undefined}
          placeholder="Late night drive"
          maxLength={255}
          autoFocus
          autoComplete="off"
        />

        <Input
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          hint="Optional."
          placeholder="What is this playlist for?"
          autoComplete="off"
        />

        {formError ? (
          <p role="alert" className="text-sm text-danger">
            {formError}
          </p>
        ) : null}
      </form>
    </Dialog>
  )
}
